import type { ShortcutBindingsMap, ShortcutPresetName, ShortcutsStoreState } from 'src/stores/shortcuts/types';

import {
	SHORTCUT_BINDINGS_KEY,
	SHORTCUT_ENABLED_KEY,
	SHORTCUT_PRESET_NAME_KEY,
} from 'src/stores/shortcuts/localStorageKeys';

import { migrateBindingIds } from 'src/util/shortcuts/bindingMigrations';
import { buildDefaultBindings } from 'src/util/shortcuts/defaultBindings';

const VALID_PRESETS: ReadonlyArray<ShortcutPresetName> = ['custom', 'minimal'];

function readBooleanWithDefault(key: string, fallback: boolean): boolean {
	const raw = localStorage.getItem(key);

	if (raw === null) {
		return fallback;
	}

	return /^true$/i.test(raw);
}

export function resolveShortcutPresetName(candidate: unknown): ShortcutPresetName {
	if (typeof candidate === 'string' && VALID_PRESETS.includes(candidate as ShortcutPresetName)) {
		return candidate as ShortcutPresetName;
	}

	return 'minimal';
}

export function sanitizeShortcutBindings(presetName: ShortcutPresetName, parsed: unknown): ShortcutBindingsMap {
	const defaults = buildDefaultBindings(presetName === 'custom' ? 'minimal' : presetName);

	if (typeof parsed !== 'object' || parsed === null) {
		return defaults;
	}

	// In-Process-Wert nach JSON.parse; Legacy-IDs und -Modifier werden zuerst migriert,
	// jede Property wird darunter per typeof geprüft.
	const stored: Record<string, { enabled?: unknown; keys?: unknown } | undefined> = migrateBindingIds(
		parsed as Record<string, { enabled: boolean; keys: string }>
	);
	const merged: ShortcutBindingsMap = { ...defaults };

	for (const actionId of Object.keys(defaults)) {
		const storedBinding = stored[actionId];

		if (
			storedBinding !== undefined &&
			typeof storedBinding.keys === 'string' &&
			typeof storedBinding.enabled === 'boolean'
		) {
			merged[actionId] = {
				enabled: storedBinding.enabled,
				id: actionId,
				keys: storedBinding.keys,
			};
		}
	}

	return merged;
}

function resolveStoredBindings(presetName: ShortcutPresetName): ShortcutBindingsMap {
	const raw = localStorage.getItem(SHORTCUT_BINDINGS_KEY);

	if (raw === null) {
		return buildDefaultBindings(presetName === 'custom' ? 'minimal' : presetName);
	}

	try {
		return sanitizeShortcutBindings(presetName, JSON.parse(raw));
	} catch {
		return buildDefaultBindings(presetName === 'custom' ? 'minimal' : presetName);
	}
}

function createDefaultShortcutsState(): ShortcutsStoreState {
	const presetName = resolveShortcutPresetName(localStorage.getItem(SHORTCUT_PRESET_NAME_KEY));

	return {
		bindings: resolveStoredBindings(presetName),
		enabled: readBooleanWithDefault(SHORTCUT_ENABLED_KEY, true),
		presetName,
	};
}

export default createDefaultShortcutsState;
